<template>
    <div>
        <div v-if="loading">
            <generic-loading />
        </div>

        <div v-else-if="verified" class="text-center">
            <font-awesome-icon
                icon="fa-light fa-check-circle"
                size="5x"
                class="text-success"
            />
            <div class="mt-3 lead">
                {{ $t("Verified") }}
            </div>
        </div>

        <div v-else-if="sessionActive">
            <div v-if="cordova" class="text-center">
                <generic-loading />
                <div class="mt-3">
                    {{
                        $t(
                            "Complete the liveness check in the opened window, then return here"
                        )
                    }}
                </div>
            </div>
            <div v-else>
                <iframe
                    :src="livenessUrl"
                    allow="camera"
                    width="100%"
                    style="border: none; border-radius: 12px; height: 80dvh"
                />
                <div class="mt-2 text-center">
                    <button
                        @click="openFallback"
                        class="btn btn-link text-sm text-secondary"
                    >
                        {{ $t("Having trouble? Open in a new tab") }}
                    </button>
                </div>
            </div>
        </div>

        <div v-else>
            <div v-if="pendingReview">
                <b-alert variant="info" show>
                    {{
                        $t(
                            "Your liveness check could not be automatically approved and has been submitted for manual review."
                        )
                    }}
                    <div class="mt-3">
                        {{
                            $t(
                                "You're welcome to submit another attempt."
                            )
                        }}
                    </div>
                </b-alert>
            </div>
            <div v-else class="text-center">
                <font-awesome-icon
                    icon="fa-duotone fa-face-viewfinder"
                    size="5x"
                    class="text-primary"
                />

                <div class="mt-3 lead">
                    {{
                        $t(
                            "Complete a quick liveness check to verify your identity"
                        )
                    }}
                </div>

                <b-alert variant="primary" show class="mt-3 text-left small">
                    <font-awesome-icon
                        icon="fa-duotone fa-shield-check"
                        class="mr-1"
                    />
                    {{
                        $t(
                            "Your liveness check is completely private. It is only used for verification and is never visible to other users on the platform."
                        )
                    }}
                </b-alert>
            </div>

            <b-alert v-if="rateLimitError" variant="danger" show class="mt-3">
                {{ rateLimitError }}
            </b-alert>

            <b-btn
                v-if="!rateLimitError"
                @click="startVerification"
                variant="primary"
                size="lg"
                pill
                block
                class="mt-3"
            >
                {{ $t("Start Verification") }}
            </b-btn>
        </div>
    </div>
</template>

<script>
import { mapState } from "vuex";

export default {
    data() {
        return {
            livenessUrl: null,
            loading: true,
            sessionActive: false,
            rateLimitError: null,
        };
    },
    created() {
        this.$store.dispatch("userLiveness/refresh").finally(() => {
            this.loading = false;
        });
    },
    mounted() {
        window.addEventListener("message", this.onLivenessMessage);
        document.addEventListener("visibilitychange", this.onVisibilityChange);
    },
    computed: {
        ...mapState("system", ["cordova"]),
        ...mapState("userLiveness", ["verified", "pendingReview"]),
    },
    methods: {
        async startVerification() {
            this.sessionActive = true;

            try {
                const response = await this.api.post(
                    "/userLiveness/createSession"
                );
                this.livenessUrl = response?.data?.livenessUrl;

                if (this.cordova) {
                    this.$store.dispatch(
                        "system/openBrowser",
                        this.livenessUrl
                    );
                }
            } catch (error) {
                this.sessionActive = false;

                if (error?.response?.data?.rateLimited) {
                    this.rateLimitError = error.response.data.error;
                }
            }
        },
        async openFallback() {
            try {
                const response = await this.api.post(
                    "/userLiveness/createSession"
                );
                this.livenessUrl = response?.data?.livenessUrl;

                this.$store.dispatch("system/openBrowser", this.livenessUrl);
            } catch (error) {
                if (error?.response?.data?.rateLimited) {
                    this.sessionActive = false;
                    this.rateLimitError = error.response.data.error;
                }
            }
        },
        onLivenessMessage(event) {
            if (event.data?.type !== "liveness-complete") return;

            this.sessionActive = false;
            this.$store.dispatch("userLiveness/refresh");
        },
        onVisibilityChange() {
            if (document.visibilityState === "visible") {
                this.$store.dispatch("userLiveness/refresh");
            }
        },
    },
    beforeDestroy() {
        window.removeEventListener("message", this.onLivenessMessage);
        document.removeEventListener("visibilitychange", this.onVisibilityChange);
    },
};
</script>
